/**
 * Display formatters for escalation status fields.
 *
 * Each formatter transforms a raw backend value into a human-readable string.
 * Format names match the "format" field in JSONC config and are applied by the
 * FieldGrid component when rendering FieldRow values.
 */

/** Converts "true"/"false" strings to "Yes"/"No". */
export function formatYesNo(value: string): string {
  if (value === 'true') return 'Yes';
  if (value === 'false') return 'No';
  return value;
}

/** Converts SCREAMING_SNAKE_CASE to Title Case (e.g. "NO_RISK" → "No Risk"). */
export function formatTitleCase(value: string): string {
  return value
    .split('_')
    .map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase())
    .join(' ');
}

const urgencyReasonLabels: Record<string, string> = {
  patient_unable_to_fill: 'Patient actively unable to fill - treatment on hand is one week or less',
  multiple_patients_impacted: 'Issue at scale - multiple patients impacted',
  repeat_escalation: 'Repeat escalation - previously resolved, now reopened',
};

/** Maps urgency reason enum values to human-readable labels. */
export function formatUrgencyReason(value: string): string {
  return urgencyReasonLabels[value] ?? formatTitleCase(value);
}

/** Formats an ISO 8601 date string as a readable date (e.g. "Jan 9, 2026"). */
export function formatDateOnly(value: string): string {
  // Date-only strings (YYYY-MM-DD) are parsed as UTC by the Date constructor,
  // which can shift the displayed day in western timezones. Append T00:00 to
  // force parsing as local time.
  const input = /^\d{4}-\d{2}-\d{2}$/.test(value) ? `${value}T00:00` : value;
  const date = new Date(input);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

/** Registry of all available formatters, keyed by the format name used in JSONC config. */
export const formatters: Record<string, (value: string) => string> = {
  yesNo: formatYesNo,
  titleCase: formatTitleCase,
  urgencyReason: formatUrgencyReason,
  dateOnly: formatDateOnly,
};
